import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_PROMPTS = [
  "What sectors do you cover?",
  "How much does it cost?",
  "I need a deep clean this week",
];

const getSessionId = () => {
  let id = localStorage.getItem("gl_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("gl_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello, I'm Goldie — Gold Link's AI receptionist. Ask me anything about our cleaning services, or I can arrange a quote for you.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", lead: false }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/receptionist/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const evt of events) {
          const line = evt.replace(/^data: /, "").trim();
          if (!line) continue;
          const data = JSON.parse(line);
          if (data.delta) {
            setMessages((m) => {
              const next = [...m];
              const last = { ...next[next.length - 1] };
              last.content += data.delta;
              next[next.length - 1] = last;
              return next;
            });
          }
          if (data.done) {
            setMessages((m) => {
              const next = [...m];
              const last = { ...next[next.length - 1] };
              last.content = last.content.split("[[")[0].trim();
              last.lead = data.lead;
              next[next.length - 1] = last;
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "Sorry, I dropped the connection for a moment. Please try again.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200, damping: 14 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gold-500 text-navy-900 grid place-items-center shadow-[0_10px_30px_-5px_rgba(245,158,11,0.5)] hover:bg-gold-400 transition-colors"
        aria-label="Chat with Goldie"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-navy-900 animate-pulse" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 sm:right-6 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] max-h-[70vh] flex flex-col bg-navy-800/95 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)]"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-navy-900/60">
              <span className="w-9 h-9 rounded-full bg-gold-500 grid place-items-center">
                <Sparkles className="w-4.5 h-4.5 text-navy-900" />
              </span>
              <div className="flex-1">
                <p className="font-heading font-bold text-sm text-ice-50">Goldie — AI Receptionist</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online 24/7
                </p>
              </div>
            </div>

            <div ref={listRef} data-testid="chat-message-list" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[220px]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gold-500 text-navy-900 rounded-br-sm font-medium"
                        : "bg-navy-700/60 text-ice-100 rounded-bl-sm"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gold-400" />
                    ) : null)}
                    {m.lead && (
                      <p data-testid="chat-lead-chip" className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-gold-400 border-t border-white/10 pt-2">
                        Details passed to our contracts team
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    data-testid={`chat-quick-${q.slice(0, 12).toLowerCase().replace(/[^a-z]/g, "-")}`}
                    onClick={() => send(q)}
                    className="font-mono text-[10px] uppercase tracking-[0.12em] text-gold-400 border border-gold-500/30 rounded-full px-3 py-1.5 hover:bg-gold-500/10 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10 bg-navy-900/60"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Goldie anything..."
                className="flex-1 bg-navy-900/80 border border-white/10 rounded-full px-4 py-2.5 text-sm text-ice-50 placeholder:text-ice-200/30 focus:outline-none focus:border-gold-500 transition-colors"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-full bg-gold-500 text-navy-900 grid place-items-center hover:bg-gold-400 transition-colors disabled:opacity-50 shrink-0"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
